import { AnchorHTMLAttributes, FC, MouseEvent, ReactNode, useMemo, useRef } from 'react'
import { Badge, Box, ExpandMoreIcon, IconType, MenuIcon, tss } from '@ds/components'
import { AlbatrossBottomNavIcon } from '@components/header/AlbatrossBottomNavIcon'
import { baseUrl } from '@utils/assets'
import EnhancedLink from '@components/EnhancedLink'

const useStyles = tss.create(({ theme }) => ({
    logo: {
        height: 28,
        width: 28,
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        textIndent: '-9999px',
        overflow: 'hidden',
        [theme.breakpoints.up('sm')]: {
            backgroundImage: `url('${baseUrl}images/logo-icon.svg')`,
        },
        [theme.breakpoints.up('md')]: {
            backgroundImage: `url('${baseUrl}images/logo-small.svg')`,
            width: 155,
            height: 38,
        },
        [theme.breakpoints.up('lg')]: {
            backgroundImage: `url('${baseUrl}images/logo.svg')`,
            width: 216,
            height: 72,
        },
    },
    tabBarRoot: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',

        // Height matches .headerItem
        height: 56,
        [theme.breakpoints.up('sm')]: {
            padding: '0 8px',
        },
        [theme.breakpoints.up('md')]: {
            height: 72,
            width: 1020,
            margin: '0 auto',
        },
        [theme.breakpoints.up('lg')]: {
            width: 1284,
        },
    },

    headerItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        flexBasis: '56px',
        height: '56px',
        padding: '0 8px',
        cursor: 'pointer',

        color: ' #1e70a1',

        /* reset button styles */
        background: 'none',
        border: 'none',

        '&:hover': {
            backgroundColor: '#eef5f8',
            textDecoration: 'none',
        },

        '&:focus': {
            outline: 'none',
        },

        // min-width: 1020px
        [theme.breakpoints.up('md')]: {
            flexBasis: '80px',
            height: '72px',
        },
    },

    headerItem__label: {
        marginTop: '4px',
        fontFamily: 'Bryant, sans-serif',
        fontSize: '10px',
        lineHeight: 1,
        fontWeight: 600,
        textTransform: 'uppercase',
        color: '#999' /* colorGrey60 */,

        // '&:isSelected': {
        //     color: '#0e3247',
        // },

        // min-width: 1020px
        [theme.breakpoints.up('md')]: {
            fontSize: '12px',
        },
    },

    isSelected: {
        backgroundColor: '#1e70a1',
        opacity: 0.1,

        // min-width: 767px
        [theme.breakpoints.up('sm')]: {
            borderBottom: '4px #ff9e1b solid',
        },
    },

    headerLogo: {
        display: 'none',

        // min-width: 769px
        [theme.breakpoints.up('sm')]: {
            display: 'flex',
            alignItems: 'center',
            marginRight: 'auto',
        },
        // min-width: 1020px
        [theme.breakpoints.up('md')]: {
            position: 'relative',
        },
    },

    headerCommunitySelector: {
        color: '#ff9e1b',
        fontFamily: 'Bryant, sans-serif',
        fontSize: '22px',
        lineHeight: 1,
        background: 'none',
        border: 'none',
        appearance: 'none',
        padding: 0,
        display: 'flex',
        alignItems: 'center',

        '&:hover': {
            cursor: 'pointer',
        },
    },

    headerCommunitySelectorWrapper: {
        position: 'relative',
        left: '4px',

        // min-width: 1020px
        [theme.breakpoints.up('md')]: {
            position: 'absolute',
            left: '45px',
            bottom: '-4px',
            whiteSpace: 'nowrap',
        },

        // min-width: 1300px
        [theme.breakpoints.up('lg')]: {
            position: 'relative',
            top: '2px',
            left: '6px',
        },
    },

    /* Positions the Community Explorer's anchor at the bottom of the navbar */
    communityExplorerAnchor: {
        position: 'absolute',
        width: '100%',
        height: '1px',
        backgroundColor: 'transparent',
        bottom: '-16px',

        // min-width: 1020px
        [theme.breakpoints.up('md')]: {
            bottom: '-13px',
        },

        // min-width: 1300px
        [theme.breakpoints.up('md')]: {
            bottom: '-23px',
        },
    },

    autoMarginLeft: {
        marginLeft: 'auto',
    },

    headerLogoRestricted: {
        display: 'flex',
        alignItems: 'center',
        marginRight: 'auto',

        // min-width: 1020px
        [theme.breakpoints.up('sm')]: {
            position: 'relative',
        },
    },
}))

const HeaderLogo: FC<{ children: ReactNode; disableNavigation?: boolean; isHardDunning?: boolean }> = ({
    children,
    disableNavigation,
    isHardDunning,
}) => {
    const { classes } = useStyles()

    return (
        <Box className={classes.headerLogo}>
            <EnhancedLink
                to={disableNavigation || isHardDunning ? '/subscription/renewal' : '/start'}
                title="Click to return to the InterNations homepage"
                className={classes.logo}
                aria-label="Click to return to the InterNations homepage"
            >
                Click to return to the InterNations homepage
            </EnhancedLink>
            {children}
        </Box>
    )
}

type HeaderLogoWithCommunityProps = {
    localcommunityName?: string
    disableNavigation?: boolean
    isHardDunning?: boolean
}

const expandMoreIcon = <ExpandMoreIcon color="primary.main" />

const HeaderLogoWithCommunity: FC<HeaderLogoWithCommunityProps> = ({
    localcommunityName,
    disableNavigation,
    isHardDunning,
}) => {
    const explorerAnchor = useRef<HTMLDivElement>(null)
    const { classes, cx } = useStyles()
    const classesToApply = cx(classes.headerCommunitySelectorWrapper, disableNavigation && classes.disabled)

    return (
        <HeaderLogo disableNavigation={disableNavigation} isHardDunning={isHardDunning}>
            <Box className={classesToApply}>
                <div className={classes.communityExplorerAnchor} ref={explorerAnchor} />
                <Box className={classes.headerCommunitySelector}>{localcommunityName}</Box>
            </Box>
        </HeaderLogo>
    )
}

type TabBarItemProps = {
    Icon: IconType
    route: string
    counter?: number
    selected?: boolean
    spaceLeft?: boolean
    children?: ReactNode
    desktopOnly?: boolean
    onClick?: (event: MouseEvent<HTMLElement>) => void
    testId?: string
    showAlbatrossBottomNavIcon?: ReactNode | undefined
    className?: string
} & AnchorHTMLAttributes<HTMLAnchorElement>

export const TabBarItem: FC<TabBarItemProps> = ({
    Icon,
    route,
    counter,
    selected,
    spaceLeft,
    onClick,
    desktopOnly,
    children,
    testId,
    showAlbatrossBottomNavIcon,
    className,
    ...anchorProps
}) => {
    const { cx, classes } = useStyles()
    const icon = useMemo(
        () =>
            counter ? (
                <Badge badgeContent={counter} color="error" data-testid={`nav_badge_${route.id}`}>
                    <Icon color="primary.main" />
                </Badge>
            ) : !showAlbatrossBottomNavIcon ? (
                <Icon color="primary.main" />
            ) : (
                <AlbatrossBottomNavIcon />
            ),
        [Icon, counter, showAlbatrossBottomNavIcon],
    )
    return (
        <a
            {...anchorProps}
            href={route}
            className={cx(
                classes.headerItem,
                selected && classes.isSelected,
                spaceLeft && classes.autoMarginLeft,
                desktopOnly && 'desktop-only',
                className,
            )}
            onClick={onClick}
            data-testid={testId}
        >
            {icon}
            {children ? <span className={classes.headerItem__label}>{children}</span> : null}
        </a>
    )
}

type DesktopTabBarFlyoutTriggerProps = {
    onClick: (event: MouseEvent<HTMLButtonElement>) => void
    children?: ReactNode
}

export const DesktopTabBarFlyoutTrigger: FC<DesktopTabBarFlyoutTriggerProps> = ({ onClick, children }) => {
    const { classes } = useStyles()
    return (
        <Box
            component="button"
            className={`${classes.headerItem} desktop-only`}
            onClick={onClick}
            data-testid="headerProfileFlyoutLink"
        >
            <Box display="flex" alignItems="center">
                {children}
                {expandMoreIcon}
            </Box>
        </Box>
    )
}

type TabBarProps = {
    onMenuOpen?: () => void
    localcommunityName?: string
    children?: ReactNode
    disableNavigation?: boolean
    isHardDunning?: boolean
}

const MobileMoreMenu: FC<Pick<TabBarProps, 'onMenuOpen'>> = ({ onMenuOpen }) => {
    const { classes } = useStyles()

    // No badge count for now!
    return (
        <Box
            component="button"
            className={`${classes.headerItem} mobile-only t-offCanvasMenuTrigger`}
            onClick={onMenuOpen}
            data-testid="offCanvasMenuTrigger"
            type="button"
        >
            <MenuIcon color="primary.main" />
            <label className={classes.headerItem__label}>More</label>
        </Box>
    )
}

export const TabBar: FC<TabBarProps> = ({
    onMenuOpen,
    children,
    localcommunityName,
    disableNavigation,
    isHardDunning,
}) => {
    const { classes } = useStyles()
    const more = useMemo(() => <MobileMoreMenu onMenuOpen={onMenuOpen} />, [onMenuOpen])

    return (
        <nav className={classes.tabBarRoot} data-testid="tabBar">
            <HeaderLogoWithCommunity
                localcommunityName={localcommunityName}
                disableNavigation={disableNavigation}
                isHardDunning={isHardDunning}
            />
            {children}
            {more}
        </nav>
    )
}
